/**
 * Page /admin/notifications — Catalogue des notifications de la commune.
 *
 * Répond à « qui reçoit quoi, et comment ? » et, depuis le lot N3, permet de le
 * régler.
 *
 * ─── POURQUOI ICI, ET PAS DANS LA RH ────────────────────────────────────────
 *
 * Le catalogue couvre TOUTES les briques (urbanisme, matériel, associations,
 * SMS, scolaire, cimetière…). Le loger dans les réglages RH obligerait à
 * chercher le paramétrage d'une alerte d'urbanisme dans les ressources
 * humaines. Il vit donc avec les autres réglages transverses de la commune —
 * Modules, Permissions, Conservation RGPD.
 *
 * ─── CE QUE LE MAIRE PEUT ET NE PEUT PAS ────────────────────────────────────
 *
 *   · Les CANAUX (e-mail, notification mobile) : librement réglables. Couper un
 *     e-mail ne prive personne — la cloche reste, et elle est la garantie.
 *     L'e-mail est une ceinture-bretelles.
 *   · Les DESTINATAIRES : pas modifiables. C'est une décision métier (« un dépôt
 *     d'urbanisme concerne les managers urbanisme »), pas un réglage. Laisser
 *     vider une cible d'un clic recréerait le « personne ne reçoit » que le
 *     comptage rend justement visible.
 *   · SE RETIRER soi-même : possible, parce qu'un admin est destinataire par
 *     défaut de tout — effet du code, pas de son poste. Avec un avertissement
 *     s'il est le dernier, et un motif écrit dans ce cas.
 */

import { useEffect, useState, useCallback } from 'react';
import { Card, CardBody, Badge, Alert, SectionEyebrow } from '@/components/ui';
import { useConfirmDialog } from '@/lib/use-confirm-dialog';
import { api, ApiException, type CatalogueNotificationEntree } from '@/lib/api';
import { Bell, Loader2, Mail, Smartphone, Users, Info, BellOff, RotateCcw } from 'lucide-react';

/** Interrupteur de canal. Désactivé = grisé, pas caché : l'absence est une info. */
function CanalToggle({
  actif, onToggle, icon: Icon, label, busy,
}: {
  actif: boolean;
  onToggle: () => void;
  icon: typeof Mail;
  label: string;
  busy: boolean;
}) {
  return (
    <button
      onClick={onToggle}
      disabled={busy}
      title={`${label} : ${actif ? 'activé' : 'désactivé'} — cliquer pour changer`}
      aria-pressed={actif}
      className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-xs transition-colors disabled:opacity-50 ${
        actif
          ? 'border-tech/30 bg-tech-tint text-tech-fg'
          : 'border-border bg-surface text-ink-muted'
      }`}
    >
      <Icon className="h-3.5 w-3.5" />
      {label}
    </button>
  );
}

export function NotificationsCataloguePage() {
  const { confirm, prompt, dialogElement } = useConfirmDialog();
  const [entrees, setEntrees] = useState<CatalogueNotificationEntree[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [filtre, setFiltre] = useState<'tous' | 'metier' | 'administre'>('tous');

  const load = useCallback(async () => {
    try {
      const r = await api.admin.catalogueNotifications();
      setEntrees(r.entrees);
      setError(null);
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Chargement impossible.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Filtre pré-appliqué via ?contexte= — permet d'arriver sur une vue réduite.
    const p = new URLSearchParams(window.location.search).get('contexte');
    if (p === 'metier' || p === 'administre') setFiltre(p);
    void load();
  }, [load]);

  async function toggleCanal(e: CatalogueNotificationEntree, canal: 'email' | 'push') {
    setBusy(e.code);
    try {
      await api.admin.setCanalNotification(e.code, canal, !e.canaux[canal]);
      await load();
    } catch (err) {
      setError(err instanceof ApiException ? err.message : 'Enregistrement impossible.');
    } finally {
      setBusy(null);
    }
  }

  async function resetEvenement(e: CatalogueNotificationEntree) {
    setBusy(e.code);
    try {
      // `null` = revenir au défaut éditeur, ce qui n'est pas « tout désactiver ».
      // La commune retrouve alors les évolutions futures des défauts.
      await api.admin.setCanalNotification(e.code, 'email', null);
      await api.admin.setCanalNotification(e.code, 'push', null);
      await load();
    } catch (err) {
      setError(err instanceof ApiException ? err.message : 'Réinitialisation impossible.');
    } finally {
      setBusy(null);
    }
  }

  async function toggleDesabonnement(e: CatalogueNotificationEntree) {
    // Se réabonner : aucun risque, aucune question.
    if (e.desabonne) {
      setBusy(e.code);
      try {
        await api.admin.setDesabonnementNotification(e.code, false);
        await load();
      } catch (err) {
        setError(err instanceof ApiException ? err.message : 'Enregistrement impossible.');
      } finally {
        setBusy(null);
      }
      return;
    }

    // Se retirer : on mesure d'abord l'impact côté serveur.
    let impact: { dernier: boolean; restant: number };
    try {
      impact = await api.admin.impactDesabonnement(e.code);
    } catch (err) {
      setError(err instanceof ApiException ? err.message : 'Vérification impossible.');
      return;
    }

    let motif: string | undefined;
    if (impact.dernier) {
      // Le seul cas où le geste prive la commune : plus personne ne recevra
      // l'événement. On n'interdit pas — il peut y avoir une vraie raison — mais
      // l'acte doit être délibéré et tracé.
      const saisi = await prompt({
        title: 'Vous êtes le dernier destinataire',
        description:
          `Si vous vous retirez de « ${e.libelle} », plus personne dans la commune ne ` +
          `recevra cette notification. Indiquez pourquoi : ce motif sera conservé dans ` +
          `le journal d'activité.`,
        placeholder: 'Ex. : service externalisé au syndicat mixte depuis janvier',
        multiline: true,
        required: true,
        maxLength: 500,
        confirmLabel: 'Me retirer quand même',
      });
      if (saisi === null) return;
      motif = saisi;
    } else {
      const ok = await confirm({
        title: 'Ne plus recevoir cette notification ?',
        description:
          `Vous ne recevrez plus « ${e.libelle} ». ${impact.restant} autre` +
          `${impact.restant > 1 ? 's personnes la recevront' : ' personne la recevra'} ` +
          `toujours. Vous pourrez revenir sur ce choix à tout moment.`,
        confirmLabel: 'Me retirer',
      });
      if (!ok) return;
    }

    setBusy(e.code);
    try {
      await api.admin.setDesabonnementNotification(e.code, true, motif);
      await load();
    } catch (err) {
      setError(err instanceof ApiException ? err.message : 'Enregistrement impossible.');
    } finally {
      setBusy(null);
    }
  }

  const vues = entrees.filter((e) => filtre === 'tous' || e.contexte === filtre);
  const sansPersonne = entrees.filter((e) => e.destinatairesActuels === 0);
  const groupes = [...new Set(vues.map((e) => e.groupe))];

  return (
    <div className="space-y-4">
      <SectionEyebrow>Notifications</SectionEyebrow>

      <Alert variant="info" icon={Info}>
        Réglez ici <strong>par quel moyen</strong> partent les notifications de votre
        commune. La cloche dans l'application est toujours active : c'est elle qui
        garantit que l'information arrive. L'e-mail et la notification mobile
        viennent en complément.
      </Alert>

      {sansPersonne.length > 0 && (
        <Alert variant="warning">
          {sansPersonne.length === 1
            ? 'Une notification n’a aucun destinataire : personne ne la recevra.'
            : `${sansPersonne.length} notifications n’ont aucun destinataire : personne ne les recevra.`}{' '}
          Vérifiez que les services concernés ont bien un responsable.
        </Alert>
      )}

      {error && <Alert variant="error">{error}</Alert>}

      <div className="flex flex-wrap gap-2">
        {([
          ['tous', 'Toutes'],
          ['metier', 'Notifications de travail'],
          ['administre', 'Notifications aux habitants'],
        ] as const).map(([k, label]) => (
          <button
            key={k}
            onClick={() => setFiltre(k)}
            className={`rounded-lg px-3 py-1.5 text-sm transition-colors ${
              filtre === k ? 'bg-tech text-white' : 'bg-surface text-ink hover:bg-surface/80'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-ink-muted" />
        </div>
      ) : (
        groupes.map((groupe) => (
          <div key={groupe} className="space-y-2">
            <h3 className="pt-2 text-sm font-semibold text-ink-muted">{groupe}</h3>
            {vues.filter((e) => e.groupe === groupe).map((e) => (
              <Card key={e.code}>
                <CardBody className="space-y-2.5">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-semibold">{e.libelle}</p>
                      <p className="text-sm text-ink-muted">{e.declencheur}</p>
                    </div>
                    <div className="flex flex-shrink-0 items-center gap-1.5">
                      {e.personnalise && <Badge variant="info" size="sm">Personnalisé</Badge>}
                      <Badge variant={e.contexte === 'metier' ? 'neutral' : 'success'} size="sm">
                        {e.contexte === 'metier' ? 'Travail' : 'Habitant'}
                      </Badge>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-2 text-sm">
                    <span className="inline-flex items-center gap-1.5 text-ink-muted">
                      <Users className="h-3.5 w-3.5" />
                      {e.cibleLabel}
                    </span>
                    {e.destinatairesActuels !== null && (
                      <Badge variant={e.destinatairesActuels === 0 ? 'urgent' : 'neutral'} size="sm">
                        {e.destinatairesActuels === 0
                          ? 'personne'
                          : `${e.destinatairesActuels} destinataire${e.destinatairesActuels > 1 ? 's' : ''}`}
                      </Badge>
                    )}
                  </div>

                  <div className="flex flex-wrap items-center gap-1.5 border-t border-border/60 pt-2.5">
                    <span className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-surface px-2.5 py-1 text-xs text-ink-muted">
                      <Bell className="h-3.5 w-3.5" />
                      Cloche · toujours
                    </span>
                    <CanalToggle
                      actif={e.canaux.email} icon={Mail} label="E-mail"
                      busy={busy === e.code} onToggle={() => void toggleCanal(e, 'email')}
                    />
                    <CanalToggle
                      actif={e.canaux.push} icon={Smartphone} label="Mobile"
                      busy={busy === e.code} onToggle={() => void toggleCanal(e, 'push')}
                    />

                    <span className="ml-auto flex items-center gap-1.5">
                      {e.personnalise && (
                        <button
                          onClick={() => void resetEvenement(e)}
                          disabled={busy === e.code}
                          title="Revenir aux réglages par défaut"
                          className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs text-ink-muted hover:bg-surface disabled:opacity-50"
                        >
                          <RotateCcw className="h-3.5 w-3.5" />
                          Défaut
                        </button>
                      )}
                      {/* Le désabonnement individuel n'a de sens qu'en contexte
                          métier : côté habitant, les canaux relèvent des
                          préférences personnelles, pas de cet écran. */}
                      {e.contexte === 'metier' && (
                        <button
                          onClick={() => void toggleDesabonnement(e)}
                          disabled={busy === e.code}
                          className={`inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs disabled:opacity-50 ${
                            e.desabonne
                              ? 'bg-urgent-tint text-urgent'
                              : 'text-ink-muted hover:bg-surface'
                          }`}
                        >
                          <BellOff className="h-3.5 w-3.5" />
                          {e.desabonne ? 'Retiré — réactiver' : 'Ne plus recevoir'}
                        </button>
                      )}
                    </span>
                  </div>
                </CardBody>
              </Card>
            ))}
          </div>
        ))
      )}
      {dialogElement}
    </div>
  );
}
